import React, { useEffect, useState, useRef } from 'react';
import $ from "jquery";
import LoadingSpinnerImage from "../Shared/LoadingSpinnerImage.js";
import { Tags, TagList } from "../Shared/Tags.js";
import { CancelButton, SaveButton, DeleteButton, PrimaryPillButton, ActionButtonsRow } from "./ActionButtons.js";
import "trumbowyg/dist/ui/trumbowyg.min.css";

const STYLE_OPTIONS = [
    { value: 1, label: "Blue" },
    { value: 3, label: "Green" },
    { value: 0, label: "Yellow" },
    { value: 2, label: "Red" },
];

const truncateText = (value, maxLength = 190) => {
    if (!value) return "";
    return value.length > maxLength ? value.substring(0, maxLength) + "..." : value;
};

const stripHtml = (html) => {
    if (!html) return "";
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || "").replace(/\s+/g, " ").trim();
};

const NotesTab = ({ workspaceId }) => {

    const [notes, setNotes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [popupMode, setPopupMode] = useState(null); // null | "create" | "view" | "edit"
    const [editingNote, setEditingNote] = useState(null);
    const [title, setTitle] = useState("");
    const [style, setStyle] = useState(1);
    const [text, setText] = useState("");
    const trumbowygRef = useRef(null);
    const editorReadyRef = useRef(false);

    const noteTagObjectType = `workspace_${workspaceId}_note`;

    const loadNotes = () => {
        setIsLoading(true);
        setError(null);
        fetch(`/umbraco/api/WatchlistsApi/WatchlistNotes?workspaceId=${workspaceId}`)
            .then((response) => {
                if (!response.ok) throw new Error("Could not load intel blocks");
                return response.json();
            })
            .then((data) => setNotes(data || []))
            .catch((err) => setError(err.message))
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        loadNotes();
        closePopup();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workspaceId]);

    const destroyEditor = () => {
        if (trumbowygRef.current && window.$) {
            const $editor = window.$(trumbowygRef.current);
            if ($editor.data("trumbowyg"))
                $editor.trumbowyg("destroy");
        }
        editorReadyRef.current = false;
    };

    const closePopup = () => {
        destroyEditor();
        setPopupMode(null);
        setEditingNote(null);
        setTitle("");
        setStyle(1);
        setText("");
        setError(null);
    };

    const openCreate = () => {
        destroyEditor();
        setPopupMode("create");
        setEditingNote(null);
        setTitle("");
        setStyle(1);
        setText("");
        setError(null);
    };

    const openView = (note) => {
        destroyEditor();
        setPopupMode("view");
        setEditingNote({ ...note, Tags: note.Tags || [] });
        setTitle(note.Title || "");
        setStyle(note.Style != null ? note.Style : 1);
        setText(note.Text || "");
        setError(null);
    };

    const openEdit = (note = editingNote) => {
        if (!note) return;
        destroyEditor();
        setPopupMode("edit");
        setEditingNote({ ...note, Tags: note.Tags || [] });
        setTitle(note.Title || "");
        setStyle(note.Style != null ? note.Style : 1);
        setText(note.Text || "");
        setError(null);
    };

    // Init / refresh Trumbowyg when editing
    useEffect(() => {
        if (popupMode !== "edit")
            return undefined;

        let cancelled = false;

        const initEditor = async () => {
            if (!trumbowygRef.current)
                return;

            window.$ = $;
            window.jQuery = $;
            await import("trumbowyg");

            if (cancelled || !trumbowygRef.current)
                return;

            const $editor = $(trumbowygRef.current);
            if ($editor.data("trumbowyg"))
                $editor.trumbowyg("destroy");

            $editor.trumbowyg({
                btns: [
                    ["viewHTML"],
                    ["formatting"],
                    ["strong", "em", "del"],
                    ["superscript", "subscript"],
                    ["link"],
                    ["insertImage"],
                    ["justifyLeft", "justifyCenter", "justifyRight", "justifyFull"],
                    ["unorderedList", "orderedList"],
                    ["horizontalRule"],
                    ["removeformat"],
                    ["fullscreen"],
                ],
                autogrow: true,
            });

            $editor.trumbowyg("html", text || "");
            $editor.on("tbwchange", () => {
                setText($editor.trumbowyg("html") || "");
            });
            editorReadyRef.current = true;
        };

        const timer = setTimeout(initEditor, 0);

        return () => {
            cancelled = true;
            clearTimeout(timer);
            destroyEditor();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [popupMode, editingNote?.Id]);

    const getEditorHtml = () => {
        if (trumbowygRef.current && editorReadyRef.current) {
            const $editor = $(trumbowygRef.current);
            if ($editor.data("trumbowyg"))
                return $editor.trumbowyg("html") || "";
        }
        return text || "";
    };

    const saveCreate = () => {
        if (!title.trim()) {
            setError("Title is required");
            return;
        }
        setIsSaving(true);
        setError(null);
        const formData = new FormData();
        formData.append("Title", title.trim());
        formData.append("Text", "");
        formData.append("Style", String(style));
        formData.append("IntelligenceWorkspaceId", workspaceId);

        fetch("/umbraco/api/WatchlistsApi/WatchlistNotes", { method: "POST", body: formData })
            .then(async (response) => {
                if (!response.ok) {
                    const message = await response.text();
                    throw new Error(message || "Could not create intel block");
                }
                return response.json();
            })
            .then((created) => {
                loadNotes();
                openEdit(created);
            })
            .catch((err) => setError(err.message))
            .finally(() => setIsSaving(false));
    };

    const saveEdit = () => {
        if (!editingNote?.Id) return;
        if (!title.trim()) {
            setError("Title is required");
            return;
        }
        setIsSaving(true);
        setError(null);
        const formData = new FormData();
        formData.append("Id", editingNote.Id);
        formData.append("Title", title.trim());
        formData.append("Text", getEditorHtml());
        formData.append("Style", String(style));
        formData.append("IntelligenceWorkspaceId", workspaceId);

        fetch("/umbraco/api/WatchlistsApi/WatchlistNotes", { method: "PUT", body: formData })
            .then(async (response) => {
                if (!response.ok) {
                    const message = await response.text();
                    throw new Error(message || "Could not save intel block");
                }
                return response.json();
            })
            .then((saved) => {
                loadNotes();
                openView(saved || {
                    ...editingNote,
                    Title: title.trim(),
                    Text: getEditorHtml(),
                    Style: style,
                });
            })
            .catch((err) => setError(err.message))
            .finally(() => setIsSaving(false));
    };

    const deleteNote = () => {
        if (!editingNote?.Id) return;
        if (!window.confirm("Delete this intel block?")) return;
        setIsSaving(true);
        fetch(`/umbraco/api/WatchlistsApi/WatchlistNotes?id=${editingNote.Id}`, { method: "DELETE" })
            .then((response) => {
                if (!response.ok) throw new Error("Could not delete intel block");
                closePopup();
                loadNotes();
            })
            .catch((err) => setError(err.message))
            .finally(() => setIsSaving(false));
    };

    const tagsContext = {
        currentItem: editingNote,
        setCurrentItem: setEditingNote,
        userRole: window.userRole || "Admin",
    };

    const renderPopup = () => {
        if (!popupMode)
            return null;

        return (
            <div
                className="watchlists-note-popup-overlay"
                onClick={(e) => {
                    if (e.target === e.currentTarget && !isSaving)
                        closePopup();
                }}
            >
                <div className="add-task-popup watchlists-note-popup">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px", marginBottom: "12px" }}>
                        <h3 style={{ margin: 0 }}>
                            {popupMode === "create" ? "Create Intel Block" : "Intel Block"}
                        </h3>
                        <button type="button" className="close-button" onClick={closePopup} aria-label="Close" disabled={isSaving}>
                            <i className="fas fa-times" />
                        </button>
                    </div>

                    {error && <div style={{ color: "#e53e3e", marginBottom: "10px" }}>{error}</div>}

                    {popupMode === "create" && (
                        <div className="watchlists-note-editor" style={{ border: "none", padding: 0, boxShadow: "none" }}>
                            <div style={{ marginBottom: "14px" }}>
                                <label className="watchlists-note-label">Title</label>
                                <textarea className="form-control" rows={2} placeholder="Enter Title" value={title} onChange={(e) => setTitle(e.target.value)} style={{ width: "100%" }} />
                            </div>
                            <div style={{ marginBottom: "14px" }}>
                                <label className="watchlists-note-label">Color</label>
                                <select className="form-control rounded-0" value={style} onChange={(e) => setStyle(parseInt(e.target.value, 10))}>
                                    {STYLE_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                                <div className={`watchlists-note-style-preview intelligence-note-block-style-${style}`} />
                            </div>
                            <ActionButtonsRow isLoading={isSaving}>
                                <CancelButton onClick={closePopup} />
                                <SaveButton onClick={saveCreate} />
                            </ActionButtonsRow>
                        </div>
                    )}

                    {popupMode === "view" && editingNote && (
                        <div>
                            <div className={`watchlists-note-style-line intelligence-note-block-style-${style}`} />
                            <div style={{ marginBottom: "14px" }}>
                                <label className="watchlists-note-label">Created by</label>
                                <div>{editingNote.CreatedByName || "Unknown"}</div>
                            </div>
                            <div style={{ marginBottom: "14px" }}>
                                <label className="watchlists-note-label">Title</label>
                                <div style={{ fontWeight: 600 }}>{title || "—"}</div>
                            </div>
                            <div style={{ marginBottom: "14px" }}>
                                <label className="watchlists-note-label">Color</label>
                                <div>{STYLE_OPTIONS.find((o) => o.value === style)?.label || "—"}</div>
                            </div>
                            {(editingNote.Tags || []).length > 0 && (
                                <div style={{ marginBottom: "14px" }}>
                                    <label className="watchlists-note-label">Tags</label>
                                    <TagList tags={editingNote.Tags} />
                                </div>
                            )}
                            <div style={{ marginBottom: "16px" }}>
                                <label className="watchlists-note-label">Description</label>
                                <div
                                    className="watchlists-note-view-body"
                                    dangerouslySetInnerHTML={{ __html: text || "<em>No description</em>" }}
                                />
                            </div>
                            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                <PrimaryPillButton onClick={() => openEdit(editingNote)}>
                                    <i className="far fa-edit pointer g-mr-8"></i>Edit
                                </PrimaryPillButton>
                            </div>
                        </div>
                    )}

                    {popupMode === "edit" && editingNote && (
                        <div className="watchlists-note-editor" style={{ border: "none", padding: 0, boxShadow: "none" }}>
                            <div className={`watchlists-note-style-line intelligence-note-block-style-${style}`} />

                            <div style={{ marginBottom: "14px" }}>
                                <label className="watchlists-note-label">Created by</label>
                                <div>{editingNote.CreatedByName || "Unknown"}</div>
                            </div>

                            <div style={{ marginBottom: "14px" }}>
                                <label className="watchlists-note-label">Title</label>
                                <input type="text" className="form-control" value={title} onChange={(e) => setTitle(e.target.value)} style={{ width: "100%" }} />
                            </div>

                            <div style={{ marginBottom: "14px" }}>
                                <label className="watchlists-note-label">Color</label>
                                <select className="form-control rounded-0" value={style} onChange={(e) => setStyle(parseInt(e.target.value, 10))}>
                                    {STYLE_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div style={{ marginBottom: "14px" }}>
                                <label className="watchlists-note-label">Tags</label>
                                <div className="watchlists-note-tags tag-container">
                                    <Tags context={tagsContext} objectType={noteTagObjectType} />
                                </div>
                            </div>

                            <div style={{ marginBottom: "14px" }}>
                                <label className="watchlists-note-label">Text</label>
                                <textarea
                                    ref={trumbowygRef}
                                    defaultValue={text}
                                    placeholder="Enter your note text here"
                                    style={{ width: "100%", minHeight: "120px" }}
                                />
                            </div>

                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                {!isSaving ? <DeleteButton onClick={deleteNote} /> : <span />}
                                <ActionButtonsRow isLoading={isSaving}>
                                    <CancelButton onClick={() => openView({
                                        ...editingNote,
                                        Title: title,
                                        Text: text,
                                        Style: style,
                                    })} />
                                    <SaveButton onClick={saveEdit} />
                                </ActionButtonsRow>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div>
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginBottom: "12px" }}>
                <PrimaryPillButton onClick={openCreate}>
                    <i className="fas fa-plus pointer g-mr-8"></i>Add Intel Block
                </PrimaryPillButton>
            </div>

            {error && !popupMode && <div style={{ color: "#e53e3e", marginBottom: "10px" }}>{error}</div>}

            {isLoading ? <LoadingSpinnerImage /> : (
                notes.length === 0 ? (
                    <div className="watchlists-empty">No intel blocks yet.</div>
                ) : (
                    <div className="intelligence-notes-container">
                        {notes.map((note) => {
                            const descriptionPreview = truncateText(stripHtml(note.Text), 160);
                            return (
                                <div
                                    key={note.Id}
                                    className={`intelligence-note-block intelligence-note-block-style-${note.Style}`}
                                    onClick={() => openView(note)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => { if (e.key === "Enter") openView(note); }}
                                >
                                    <div className="watchlists-note-card-title">
                                        {truncateText(note.Title)}
                                    </div>
                                    {descriptionPreview ? (
                                        <div className="watchlists-note-card-preview">
                                            {descriptionPreview}
                                        </div>
                                    ) : null}
                                    <div className="watchlists-note-card-meta ellipsis">
                                        <i className="fas fa-user" style={{ fontSize: "0.9rem" }} /> Created by: {note.CreatedByName || "Unknown"}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )
            )}

            {renderPopup()}
        </div>
    );
};

export default NotesTab;
